import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import { buildTypePillClass, initials } from '../../lib/workbench';
import {
  createDefect,
  completeDefectAssignment,
  completeDefectWork,
  approveDefectRetest,
  fetchOpenDefectTasks,
} from '../../lib/tasks';

const DEFECT_STATUS_LABEL = {
  open: 'Open',
  assignment_pending: 'Assign fix',
  in_progress: 'In progress',
  retest: 'Retest',
  closed: 'Closed',
};

function defectStatusPillClass(status) {
  if (status === 'closed') return 'done';
  if (status === 'retest' || status === 'in_progress') return 'progress';
  return 'pending';
}

export default function Defects() {
  const { project } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const focusDefectId = searchParams.get('focusDefect');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defects, setDefects] = useState([]);
  const [buildItems, setBuildItems] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [assignDefect, setAssignDefect] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newBuildItemId, setNewBuildItemId] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [defectRes, itemsRes, profRes, taskRows] = await Promise.all([
        sb
          .from('defects')
          .select(`
            id,
            ref,
            title,
            description,
            status,
            updated_at,
            deliverables:build_item_id (
              ref,
              title,
              deliverable_types ( name )
            )
          `)
          .eq('project_id', project.id)
          .order('updated_at', { ascending: false }),
        sb
          .from('deliverables')
          .select('id, ref, title, deliverable_types(name)')
          .eq('project_id', project.id)
          .eq('kind', 'build_item')
          .order('ref'),
        sb.from('profiles').select('id, full_name').order('full_name'),
        fetchOpenDefectTasks(project.id),
      ]);
      setDefects(defectRes.data || []);
      setBuildItems(itemsRes.data || []);
      setProfiles(profRes.data || []);
      setOpenTasks(taskRows);
    } catch (e) {
      console.error('[Defects] load failed', e);
      showToast('Failed to load defects');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { load(); }, [load]);

  const tasksByDefect = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      if (!map.has(t.source_artefact_id)) map.set(t.source_artefact_id, {});
      map.get(t.source_artefact_id)[t.task_type] = t;
    }
    return map;
  }, [openTasks]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return defects;
    return defects.filter((d) => d.status === statusFilter);
  }, [defects, statusFilter]);

  const stats = useMemo(() => ({
    open: defects.filter((d) => d.status !== 'closed').length,
    retest: defects.filter((d) => d.status === 'retest').length,
    closed: defects.filter((d) => d.status === 'closed').length,
  }), [defects]);

  async function handleCreate() {
    if (!newBuildItemId || !newTitle.trim()) return;
    setSaving(true);
    try {
      await createDefect(newBuildItemId, newTitle.trim(), newDescription.trim() || null);
      showToast('Defect logged', 'success');
      setShowCreate(false);
      setNewTitle('');
      setNewDescription('');
      setNewBuildItemId('');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to create defect');
    } finally {
      setSaving(false);
    }
  }

  async function confirmAssignment() {
    if (!assignDefect || !selectedMember) return;
    const task = tasksByDefect.get(assignDefect.id)?.assignment;
    if (!task) return;
    setSaving(true);
    try {
      await completeDefectAssignment(task.id, selectedMember);
      showToast('Fix task created', 'success');
      setAssignDefect(null);
      setSelectedMember(null);
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to assign fix');
    } finally {
      setSaving(false);
    }
  }

  async function handleCompleteWork(defect) {
    const workTask = tasksByDefect.get(defect.id)?.work;
    if (!workTask) return;
    setSaving(true);
    try {
      await completeDefectWork(workTask.id);
      showToast('Fix submitted for retest', 'success');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to complete fix');
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveRetest(defect) {
    const approvalTask = tasksByDefect.get(defect.id)?.approval;
    if (!approvalTask) return;
    setSaving(true);
    try {
      await approveDefectRetest(approvalTask.id);
      showToast('Defect closed', 'success');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to close defect');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading defects…" />;

  return (
    <div className="wb-tracker-shell">
      <div className="wb-review-head">
        <div>
          <div className="wb-review-title">Defect management</div>
          <div className="wb-review-sub">
            Assignment routes to the build item owner · retest/close routes to Testing Lead.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="wb-run-progress">
            <div className="wb-run-stat">
              <div className="wb-run-stat-num pending">{stats.open}</div>
              <div className="wb-run-stat-lbl">Open</div>
            </div>
            <div className="wb-run-stat">
              <div className="wb-run-stat-num progress">{stats.retest}</div>
              <div className="wb-run-stat-lbl">Retest</div>
            </div>
            <div className="wb-run-stat">
              <div className="wb-run-stat-num done">{stats.closed}</div>
              <div className="wb-run-stat-lbl">Closed</div>
            </div>
          </div>
          {canWrite && (
            <button type="button" className="wb-row-action-btn primary" onClick={() => setShowCreate(true)}>
              Log defect
            </button>
          )}
        </div>
      </div>

      <div className="wb-list-panel">
        <div className="wb-list-toolbar">
          <span className="wb-list-toolbar-label">Status</span>
          {[
            ['all', 'All'],
            ['assignment_pending', 'Assign fix'],
            ['in_progress', 'In progress'],
            ['retest', 'Retest'],
            ['closed', 'Closed'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wb-filter-pill${statusFilter === key ? ' on' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <table className="wb-tracker-table">
          <thead>
            <tr>
              <th>Defect</th>
              <th>Build item</th>
              <th>Status</th>
              <th>Assignee</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((defect) => {
              const item = defect.deliverables;
              const typeName = item?.deliverable_types?.name || '—';
              const tasks = tasksByDefect.get(defect.id) || {};
              const activeTask = tasks.assignment || tasks.work || tasks.approval;
              const assignee = activeTask?.profiles?.full_name;
              const focused = focusDefectId === defect.id;

              return (
                <tr key={defect.id} className={focused ? 'task-focus' : ''}>
                  <td>
                    <div className="wb-item-ref">{defect.ref}</div>
                    <div className="wb-item-title">{defect.title}</div>
                  </td>
                  <td>
                    <div className="wb-item-ref">{item?.ref || '—'}</div>
                    <span className={`wb-type-pill ${buildTypePillClass(typeName)}`}>{typeName}</span>
                  </td>
                  <td>
                    <span className={`wb-status-pill ${defectStatusPillClass(defect.status)}`}>
                      {DEFECT_STATUS_LABEL[defect.status] || defect.status}
                    </span>
                  </td>
                  <td>
                    {assignee ? (
                      <div className="wb-owner-cell">
                        <div className="wb-owner-av">{initials(assignee)}</div>
                        {assignee}
                        <span className="wb-owner-muted"> · {activeTask.task_type}</span>
                      </div>
                    ) : (
                      <span className="wb-owner-muted">—</span>
                    )}
                  </td>
                  <td>
                    {canWrite && defect.status === 'assignment_pending' && tasks.assignment && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => {
                          setAssignDefect(defect);
                          setSelectedMember(null);
                        }}
                      >
                        Assign fixer
                      </button>
                    )}
                    {canWrite && defect.status === 'in_progress' && tasks.work && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => handleCompleteWork(defect)}
                      >
                        Submit for retest
                      </button>
                    )}
                    {canWrite && defect.status === 'retest' && tasks.approval && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => handleApproveRetest(defect)}
                      >
                        Close defect
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr>
                <td colSpan={5} className="wb-table-empty">
                  {defects.length ? 'No defects match the current filter.' : 'Log a defect against a build item to start the fix workflow.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal
          title="Log defect"
          onClose={() => setShowCreate(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="wb-modal-btn" onClick={() => setShowCreate(false)}>Cancel</button>
              <button
                type="button"
                className="wb-modal-btn primary"
                disabled={!newBuildItemId || !newTitle.trim() || saving}
                onClick={handleCreate}
              >
                Create &amp; route
              </button>
            </>
          )}
        >
          <div className="wb-modal-label">Build item</div>
          <select
            className="form-select"
            value={newBuildItemId}
            onChange={(e) => setNewBuildItemId(e.target.value)}
          >
            <option value="">Select build item…</option>
            {buildItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.ref} — {item.title}
              </option>
            ))}
          </select>
          <div className="wb-modal-label" style={{ marginTop: 12 }}>Title</div>
          <input
            className="form-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Short defect summary"
          />
          <div className="wb-modal-label" style={{ marginTop: 12 }}>Description</div>
          <textarea
            className="form-textarea"
            rows={3}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Steps to reproduce, expected vs actual…"
          />
        </Modal>
      )}

      {assignDefect && (
        <Modal
          title="Assign fixer"
          onClose={() => setAssignDefect(null)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="wb-modal-btn" onClick={() => setAssignDefect(null)}>Cancel</button>
              <button
                type="button"
                className="wb-modal-btn primary"
                disabled={!selectedMember || saving}
                onClick={confirmAssignment}
              >
                Create fix task
              </button>
            </>
          )}
        >
          <div className="wb-routing-trace">
            <b>{assignDefect.ref}</b> — {assignDefect.title}
            <br />
            Build owner assigns who will fix this defect.
          </div>
          <div className="wb-modal-label">Team member</div>
          {profiles.slice(0, 12).map((p) => (
            <button
              key={p.id}
              type="button"
              className={`wb-resource-option${selectedMember === p.id ? ' selected' : ''}`}
              onClick={() => setSelectedMember(p.id)}
            >
              <div className="wb-resource-av">{initials(p.full_name)}</div>
              <div className="wb-resource-name">{p.full_name}</div>
            </button>
          ))}
        </Modal>
      )}

      {toast && (
        <div className={`toast show ${toast.type === 'success' ? 'success' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
